/**
 * Manages a poll-interval + overall-timeout timer pair.
 *
 * Encapsulates the recurring "schedule poll, await result, decide whether to
 * continue" pattern shared by scan services. Centralizes cleanup so timers
 * cannot leak if the owning service is torn down.
 */
export class PollingTimer {
  private readonly _pollIntervalMs: number;
  private readonly _timeoutMs: number;
  private _pollTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private _overallTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private _onPoll: (() => void) | null = null;
  private _isRunning = false;

  constructor(pollIntervalMs: number, timeoutMs: number) {
    this._pollIntervalMs = pollIntervalMs;
    this._timeoutMs = timeoutMs;
  }

  get isRunning(): boolean {
    return this._isRunning;
  }

  /** Begin the polling cycle. Sets the overall timeout and schedules the first poll. */
  start(onPoll: () => void, onTimeout: () => void): void {
    this.stop();
    this._isRunning = true;
    this._onPoll = onPoll;

    this._overallTimeoutId = setTimeout(() => {
      this._overallTimeoutId = null;
      this.stop();
      onTimeout();
    }, this._timeoutMs);

    this.scheduleNext();
  }

  /** Schedule the next poll tick. Call after a poll decides to continue. */
  scheduleNext(): void {
    if (!this._isRunning) return;

    this._pollTimeoutId = setTimeout(() => {
      this._pollTimeoutId = null;
      this._onPoll?.();
    }, this._pollIntervalMs);
  }

  /** Stop all timers and reset state. Safe to call multiple times. */
  stop(): void {
    this._isRunning = false;
    this._onPoll = null;

    if (this._overallTimeoutId !== null) {
      clearTimeout(this._overallTimeoutId);
      this._overallTimeoutId = null;
    }

    if (this._pollTimeoutId !== null) {
      clearTimeout(this._pollTimeoutId);
      this._pollTimeoutId = null;
    }
  }
}
